import Image from "next/image";
import Link from "next/link";

import { ArrowRight } from "lucide-react";

interface IntroSectionProps {
  heading: string;
  body: string;
  image?: { src: string; alt: string };
  crossLink?: { text: string; href: string };
}

function highlightRebolt(text: string): React.ReactNode {
  const parts = text.split("Rebolt");
  if (parts.length === 1) return text;

  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && <span className="text-rebolt-500">Rebolt</span>}
    </span>
  ));
}

export function IntroSection({ heading, body, image, crossLink }: IntroSectionProps) {
  const paragraphs = body.split("\n\n");

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className={`${image ? "grid items-start gap-10 lg:grid-cols-2 lg:gap-16" : ""}`}>
          {image && (
            <div className="overflow-hidden rounded-2xl">
              <Image
                src={image.src}
                alt={image.alt}
                width={1024}
                height={1024}
                className="h-auto w-full object-cover"
              />
            </div>
          )}

          <div>
            <h2 className="font-clash text-3xl font-medium leading-tight tracking-snug text-ash-900 md:text-5xl">
              {highlightRebolt(heading)}
            </h2>

            <div className="mt-6 space-y-4">
              {paragraphs.map((paragraph, i) => (
                <p key={i} className="text-lg leading-relaxed text-ash-700">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>

        {crossLink && (
          <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-2xl border border-ash-200 bg-clay-100 px-6 py-5 sm:flex-row">
            <p className="text-lg text-ash-600 font-medium">{crossLink.text}</p>
            <Link
              href={crossLink.href}
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-rebolt-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-rebolt-600"
            >
              See Article
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
